import React, { useCallback, useState } from "react";
import { Modal } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";

import { Button, HookSelectFormGroup, CloseButton } from "components";
import { DebounceTimeout } from "globalConstants";
import { InstallationOutcomeEnum } from "types";
import { EnumItem } from "types/enums/customEnum";
import { required } from "validators/validation";

import CheckListRow from "./components/checkListRow";

import styles from "./modal.module.scss";

type CompleteBoardingOutcomeModalProps = {
	show?: boolean;
	onHide: () => void;
	isOpenPends: boolean;
	terminalFormValues: { [key: string]: any }[];
	id: string;
	installationComplete: (data: { id: string; option: string }) => void;
	setIsTerminalFormSubmitting: (value: boolean) => void;
	terminalFormSubmit?: () => Promise<any>;
};

const CompleteInstallationOutcomeModal = ({
	show = false,
	onHide,
	isOpenPends,
	terminalFormValues,
	id,
	installationComplete,
	setIsTerminalFormSubmitting,
	terminalFormSubmit,
}: CompleteBoardingOutcomeModalProps) => {
	const [showChecklist, setShowChecklist] = useState<boolean>(false);

	const isEmptyInstallDate = terminalFormValues.some(
		obj => obj.installDate === "",
	);

	const canCompleteApplication = !isEmptyInstallDate && !isOpenPends;

	const outcomes: EnumItem[] = InstallationOutcomeEnum.getOptions();

	const formMethods = useForm({
		mode: "onChange",
		reValidateMode: "onChange",
	});

	const {
		handleSubmit,
		formState: { errors, isDirty, isValid },
		setError,
		clearErrors,
	} = formMethods;

	const onSubmit = useCallback(
		option => {
			if (canCompleteApplication) {
				setIsTerminalFormSubmitting(true);
				terminalFormSubmit?.().then(
					() => {
						installationComplete({ id, option });
						setIsTerminalFormSubmitting(false);
					},
					() => {
						setIsTerminalFormSubmitting(false);
					},
				);
			} else {
				installationComplete({ id, option });
			}
		},
		[
			canCompleteApplication,
			id,
			installationComplete,
			setIsTerminalFormSubmitting,
			terminalFormSubmit,
		],
	);

	const handleSubmitForm = values => {
		onHide();
		onSubmit(values?.installationOutcome?.name);
	};

	const handleChangeOutcome = ({ name }) => {
		if (name === InstallationOutcomeEnum.SuccessfulInstallation.name) {
			setShowChecklist(true);
			!canCompleteApplication &&
				setError("notRegisteredInput", {
					message:
						"To change application status provide all terminals installation date and close all pends",
				});
		} else {
			setShowChecklist(false);
			clearErrors("notRegisteredInput");
		}
	};

	const getCheckList = () => {
		const installDateRow = (
			<CheckListRow
				title="Install Date provided"
				active={!isEmptyInstallDate}
			/>
		);

		const pendsRow = (
			<CheckListRow title="Pends Resolved" active={!isOpenPends} />
		);

		return (
			<ul className={styles.checkList}>
				{installDateRow}
				{pendsRow}
			</ul>
		);
	};

	return (
		<FormProvider {...formMethods}>
			<Modal
				show={show}
				onHide={onHide}
				animation={false}
				centered
				className={styles.wrapUWDecisionModal}
			>
				<form onSubmit={handleSubmit(handleSubmitForm)}>
					<Modal.Header className="pb-0">
						<Modal.Title as="h6" className="text-center mb-3">
							<div>Complete Installation</div>
						</Modal.Title>
						<CloseButton onClick={onHide} />
					</Modal.Header>
					<Modal.Body>
						<div className={styles.content}>
							<HookSelectFormGroup
								rules={{
									validate: {
										required,
									},
								}}
								label="Installation Outcome"
								options={outcomes}
								getOptionLabel={option => option.displayName}
								getOptionValue={option => option.name}
								placeholder="Please Select"
								name="installationOutcome"
								cacheOptions
								defaultOptions
								debounceTimeout={DebounceTimeout}
								colLabel={{ sm: 12 }}
								colSelect={{ sm: 12 }}
								showRequiredMark
								onChange={handleChangeOutcome}
							/>
							{showChecklist && <div>{getCheckList()}</div>}
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-center pt-0 pb-5">
						<Button
							type="submit"
							value="Complete"
							variant="primary"
							disabled={
								!isDirty ||
								!isValid ||
								!!Object.keys(errors).length
							}
						/>
					</Modal.Footer>
				</form>
			</Modal>
		</FormProvider>
	);
};

export default CompleteInstallationOutcomeModal;
